import React, {useCallback, useEffect, useRef, useState} from 'react'
import RenderImg from './editArea/Img'
import {v4 as uuidv4} from 'uuid'
import {origo} from '../utils/helpers/origo'
import Toolbox from './toolbox/Toolbox'
import {useAppDispatch, useAppSelector} from '../stores/configureStore'
import {
  changeColor,
  addImageHistoryItem,
  resetEditorStore,
  setAttribute,
  setInert,
  setInteractedImage,
  setPrintArea,
  setRotation,
  setScale,
  setZoom,
  flipSide,
  selectImage,
} from '../stores/editor/editor.reducer'
import {clamp} from '../utils/helpers/clamp'
import {Config} from '../constants/Config'
import services from '../utils/services'
import {GraphicCategory} from '../models/toolbox/graphicCategory'
import {
  resetToolboxStore,
  setGraphics,
  setGraphicCategories,
  selectToolboxTabPanel,
} from '../stores/toolbox/toolbox.reducer'
import {ImgText} from '../models/editor/imgText'
import {ImgMasked} from '../models/editor/imgMasked'
import {ProductPropertyPicker} from './toolbox/ProductPropertyPicker'
import {
  doPrintAreaContentOverflow,
  getPrintAreaClientRect,
  getPrintAreaItemsClientRects,
} from '../utils/helpers/frontendEditor'
import {Img} from '../models/editor/img'
import ShareButton from './toolbox/ShareButton'
import {
  IMG_REPOSITIONED,
  IMG_RESCALED,
  IMG_ROTATED,
} from '../models/editor/imgHistoryItemAction'
import {useRouteMatch} from 'react-router'
import {ProductColor} from '../models/dto/common/productColor'
import HistoryButtons from './editArea/HistoryButtons'
import {calculatePrice} from '../utils/helpers/price'
import {ProductPricing} from '../models/dto/common/productPricing'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faExclamationCircle, faSpinner} from '@fortawesome/free-solid-svg-icons'
import {AddToCartPage, AddToCartRequest} from '../models/dto/addToCart.request'
import ImgTools from './editArea/ImgTools'
import InsideImgTools from './editArea/InsideImgTools'
import css from '../css/editor.module.css'
import {Pos} from '../models/common/pos'
import StyleSheet from '../utils/helpers/styleSheet'
import {Page} from '../models/editor/page'
import {usePrevious} from '../utils/helpers/usePrevious'
import {DoubleProduct} from '../models/dto/getProduct.response'
import {ElementPrintAreaRect} from '../models/common/elementClientRect'
import {useQuery} from '../utils/helpers/useQuery'
import {euclideanDistance} from '../utils/helpers/euclideanDistance'
//@ts-ignore
import Lottie from 'react-lottie'
import {default as LoaderAnimation} from './icons/95692-loader-green.json'
import {SavedEditor} from '../models/dto/common/savedEditor'
import {InsideImage} from '../models/editor/insideImage'
import {createPrintArea} from '../utils/helpers/createPrintArea'
import {useTranslation} from 'react-i18next'

interface MatchParams {
  productIdString: string
}

export default function FrontendEditor() {
  const {t, i18n} = useTranslation()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const {images, zoom, interactedImage, printArea, maskImage, selectedImageId} =
    useAppSelector(store => store.editor)
  const editorRef = useRef<HTMLDivElement | null>(null)
  const zoomRef = useRef<HTMLDivElement | null>(null)
  const {productIdString} = useRouteMatch<MatchParams>(
    '/frontend/:productIdString',
  )?.params as MatchParams
  const productId = parseInt(productIdString)
  const query = useQuery()
  const sharedId = query.get('sharedId')
  const [isTwoSided, setIsTwoSided] = useState(false)
  const [textOnly, setTextOnly] = useState(false)
  const [colors, setColors] = useState<ProductColor[]>([])
  const [selectedColor, setSelectedColor] = useState<ProductColor | undefined>()
  const [mousePreviousPosition, setMousePreviousPosition] = useState({
    x: 0,
    y: 0,
  })
  const [initialTouchPosition, setInitialTouchPosition] = useState<Pos | null>(
    null,
  )
  const [initialTouchImage, setInitialTouchImage] = useState<Img | null>(null)
  const [initialTouchInsideImage, setInitialTouchInsideImage] =
    useState<InsideImage | null>(null)
  const [initialZoomLevel, setInitialZoomLevel] = useState<number | null>(null)
  const [initialTouchDistance, setInitialTouchDistance] = useState<
    number | null
  >(null)
  const [productPricing, setProductPricing] = useState<ProductPricing | null>(
    null,
  )
  const [productSizes, setProductSizes] = useState<string[] | null>(null)
  const [selectedSize, setSelectedSize] = useState('')
  const [productSlug, setProductSlug] = useState('')

  const [pages, setPages] = useState<Page[]>([])
  const [selectedPageIndex, setSelectedPageIndex] = useState<
    number | undefined
  >(undefined)
  const previousSelectedPageIndex = usePrevious(selectedPageIndex)
  const [calendar, setCalendar] = useState(false)

  useEffect(() => {
    const newColorProduct = colors.find(
      colorProduct => colorProduct.color === selectedColor?.color,
    )
    if (newColorProduct && selectedPageIndex !== undefined) {
      const newUrl = newColorProduct.pages[selectedPageIndex].imageUrl
      const newMaskUrl = newColorProduct.pages[selectedPageIndex].maskUrl
      setPages(pages =>
        pages.map((page, index) => ({
          ...page,
          images: page.images.map((image, i) =>
            i === 0
              ? {...image, url: newColorProduct.pages[index].imageUrl}
              : image,
          ),
          mask: newColorProduct.pages[index].maskUrl
            ? {
                id: uuidv4(),
                type: 'url',
                url: newColorProduct.pages[index].maskUrl as string,
                brightness: 1,
                filters: {inverse: false, monochrome: false, sepia: false},
                inert: true,
                locked: true,
                mirroredX: false,
                mirroredY: false,
                position: origo,
                rotation: 0,
                scale: 1,
              }
            : null,
        })),
      )
      dispatch(changeColor({url: newUrl, maskUrl: newMaskUrl}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedColor])

  useEffect(() => {
    if (
      previousSelectedPageIndex !== undefined &&
      productPricing &&
      selectedPageIndex !== undefined &&
      pages[selectedPageIndex].printArea
    ) {
      setPages(pages =>
        pages.map((page, index) => {
          return index === previousSelectedPageIndex
            ? {
                ...page,
                images: images,
                mask: maskImage,
                price:
                  (printArea &&
                    page.printAreaNativeWidth &&
                    page.printAreaNativeHeight &&
                    calculatePrice(
                      getPrintAreaClientRect(printArea),
                      page.printAreaNativeWidth,
                      page.printAreaNativeHeight,
                      getPrintAreaItemsClientRects(images),
                      productPricing,
                      selectedPageIndex,
                      calendar,
                      index === 1,
                      pages.length > 1 && pages[0]?.images?.length > 1,
                      previousSelectedPageIndex === 1,
                    )) ??
                  0,
                imageClientRects: getPrintAreaItemsClientRects(images),
              }
            : page
        }),
      )
      dispatch(
        flipSide({
          images: pages[selectedPageIndex].images,
          maskImage: pages[selectedPageIndex].mask,
        }),
      )
      dispatch(
        setPrintArea({
          printArea: pages[selectedPageIndex].printArea as ImgText,
        }),
      )
      setTimeout(() => {
        // This is a dirty fix that forces display price recalculation on side flip
        dispatch(setInert({inert: false}))
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedPageIndex])

  const onWheel: React.UIEventHandler<HTMLDivElement> = e => {
    if (editorRef.current) {
      //@ts-ignore
      const zoomMultipier = Math.pow(2, -e.deltaY / 300)
      const newZoomLevel = clamp(
        zoom.level * zoomMultipier,
        Config.minZoom,
        Config.maxZoom,
      )
      dispatch(
        setZoom({
          level: newZoomLevel,
          translate: {x: zoom.translate.x, y: zoom.translate.y},
        }),
      )
    }
  }

  useEffect(() => {
    if (printArea && selectedColor) {
      const newPrintArea: ImgText = {
        ...printArea,
        text: printArea?.text.replace(
          /stroke:#(?:[0-9a-fA-F]{3}){1,2}/,
          `stroke:${selectedColor.lineColor ?? '#ffffff'}`,
        ),
      }
      dispatch(setPrintArea({printArea: newPrintArea}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedColor])

  useEffect(() => {
    if (editorRef.current) {
      const editorRefCurrent = editorRef.current
      const cancelWheel = (event: WheelEvent) => event.preventDefault()

      editorRefCurrent.addEventListener('wheel', cancelWheel, {passive: false})
      return () => {
        editorRefCurrent.removeEventListener('wheel', cancelWheel)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorRef.current])

  useEffect(() => {
    const tryGetPic = async () => {
      const response = await services.getProduct(productId)
      const product = response.data
      setColors(product.colors)
      setProductSizes(product.sizes)

      setIsTwoSided(product.twoSided)
      setTextOnly(product.textOnly)
      if (product.textOnly) {
        dispatch(selectToolboxTabPanel({tabPanel: 'AddCaption'}))
      }
      setProductPricing(product.pricing)
      setCalendar(product.calendar)
      setProductSlug(product.slug)

      let saved: SavedEditor | undefined
      let savedColorIndex = 0
      if (sharedId) {
        saved = (await services.getStoredState(sharedId)).data
        savedColorIndex = product.colors.findIndex(
          item => item.color === saved?.color?.color,
        )
        if (savedColorIndex === -1) {
          savedColorIndex = 0
        }
      }

      let tempPages = product.colors[savedColorIndex].pages.map((p, index) => {
        let printWidthMm = product.pricing.szivoldalWidthMm
        let printHeightMm = product.pricing.szivoldalHeightMm
        if (product.pricing.editableArea === 'a4') {
          printWidthMm = product.pricing.a4WidthMm
          printHeightMm = product.pricing.a4HeightMm
        } else if (product.pricing.editableArea === 'a3') {
          printWidthMm = product.pricing.a3WidthMm
          printHeightMm = product.pricing.a3HeightMm
        }
        if (product.twoSided && index === 1) {
          printWidthMm = product.pricing.szivoldalWidthMm
          printHeightMm = product.pricing.szivoldalHeightMm
          if (product.pricing.editableAreaBack === 'a4') {
            printWidthMm = product.pricing.a4WidthMm
            printHeightMm = product.pricing.a4HeightMm
          } else if (product.pricing.editableAreaBack === 'a3') {
            printWidthMm = product.pricing.a3WidthMm
            printHeightMm = product.pricing.a3HeightMm
          }
        }

        const page: Page = {
          images: [
            {
              id: uuidv4(),
              type: 'url',
              url: p.imageUrl,
              brightness: 1,
              filters: {inverse: false, monochrome: false, sepia: false},
              inert: true,
              locked: true,
              mirroredX: false,
              mirroredY: false,
              position: origo,
              rotation: 0,
              scale: 1,
            },
          ],
          mask: p.maskUrl
            ? {
                id: uuidv4(),
                type: 'url',
                url: p.maskUrl,
                brightness: 1,
                filters: {inverse: false, monochrome: false, sepia: false},
                inert: true,
                locked: true,
                mirroredX: false,
                mirroredY: false,
                position: origo,
                rotation: 0,
                scale: 1,
              }
            : null,
          price: 0,
          text: p.text,
          printArea: {
            ...createPrintArea(
              printWidthMm,
              printHeightMm,
              product.colors[savedColorIndex].lineColor ?? 'black',
            ),
            position:
              product.twoSided && index === 1
                ? (product as DoubleProduct).sides.back.position
                : product.sides.front.position,
            scale:
              product.twoSided && index === 1
                ? (product as DoubleProduct).sides.back.scale
                : product.sides.front.scale,
          },
          printAreaClientRect: {
            type: 'printArea',
            // Dummy data
            x1: 377.9864501953125,
            x2: 461.1357727050781,
            y1: 123.48182678222656,
            y2: 241.06631469726562,
          },
          printAreaNativeWidth: printWidthMm,
          printAreaNativeHeight: printHeightMm,
          imageClientRects: [],
        }
        return page
      })

      if (saved && saved.pages.length === tempPages.length) {
        const typesafeSaved: SavedEditor = saved
        tempPages = tempPages.map((page, index) => ({
          ...page,
          images: [...page.images, ...typesafeSaved.pages[index].images],
          price: typesafeSaved.pages[index].price,
          imageClientRects: typesafeSaved.pages[index].imageClientRects,
        }))
      }

      setPages(tempPages)
      setSelectedPageIndex(0)

      dispatch(resetEditorStore())
      dispatch(resetToolboxStore())
      let zoomLevel = 0.2
      if (productId === 12813 || productId === 2732 || productId === 12212) {
        zoomLevel = Config.minZoom
      }
      dispatch(setZoom({level: zoomLevel, translate: origo}))
      dispatch(
        flipSide({
          images: tempPages[0].images,
          maskImage: tempPages[0].mask,
        }),
      )
      if (tempPages[0].printArea) {
        dispatch(setPrintArea({printArea: tempPages[0].printArea}))
      }
      dispatch(
        setGraphics({
          graphics: product.graphics.map(item => ({
            image: item.image,
            previewImage: item.previewImage,
            masks: item.masks,
            captionsScale: item.captions.scale,
            captions: item.captions.captions,
            category: item.category,
            width: item.width,
            height: item.height,
          })),
        }),
      )
      dispatch(
        setGraphicCategories({
          graphicCategories: product.graphics
            .map(
              (item): GraphicCategory => ({
                text: item.category,
              }),
            )
            .filter((value, i, self) => {
              return !self.slice(0, i).some(val => val.text === value.text)
            })
            .sort((item1, item2) => item1.text.localeCompare(item2.text)),
        }),
      )
      setSelectedColor(saved?.color ?? product.colors[0])
      if (saved?.size) {
        setSelectedSize(saved.size)
      } else if (product.sizes) {
        setSelectedSize(product.sizes[0])
      }
    }
    tryGetPic()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, productId])

  const addImageHistoryItemByInteraction = (): void => {
    if (!interactedImage) {
      return
    }

    const img = images.find((image: Img) => image.id === interactedImage.id)

    if (!img) {
      return
    }

    if ('scaling' === interactedImage.interaction) {
      dispatch(addImageHistoryItem({item: {img, action: IMG_RESCALED}}))
    }

    if ('rotating' === interactedImage.interaction) {
      dispatch(addImageHistoryItem({item: {img, action: IMG_ROTATED}}))
    }

    if ('dragging' === interactedImage.interaction) {
      dispatch(addImageHistoryItem({item: {img, action: IMG_REPOSITIONED}}))
    }
  }

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setMousePreviousPosition({x: e.pageX, y: e.pageY})
    if (e.target === editorRef.current) {
      dispatch(selectImage({id: undefined}))
    }
  }
  const onMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    const movementX = e.pageX - mousePreviousPosition.x
    const movementY = e.pageY - mousePreviousPosition.y
    if (interactedImage) {
      let image = images.find(image => image.id === interactedImage.id)
      if (!image) {
        return
      }
      let maskedImg = image as ImgMasked
      if (
        interactedImage.innerImageIndex !== undefined &&
        maskedImg.inside !== undefined
      ) {
        //Inside image
        switch (interactedImage.interaction) {
          case 'dragging':
            setMousePreviousPosition({x: e.pageX, y: e.pageY})
            const insideScaleCorrection =
              (1 / window.devicePixelRatio / zoom.level / maskedImg.scale) *
              maskedImg.captionsScale
            const dx = movementX * insideScaleCorrection
            const dy = movementY * insideScaleCorrection
            dispatch(
              setAttribute({
                id: interactedImage.id,
                data: {
                  inside: maskedImg.inside.map((item, i) =>
                    i === interactedImage.innerImageIndex && item
                      ? {
                          ...item,
                          position: {
                            x:
                              item.position.x +
                              dx *
                                Math.cos(
                                  (-maskedImg.rotation * Math.PI) / 180,
                                ) -
                              dy *
                                Math.sin((-maskedImg.rotation * Math.PI) / 180),
                            y:
                              item.position.y +
                              dx *
                                Math.sin(
                                  (-maskedImg.rotation * Math.PI) / 180,
                                ) +
                              dy *
                                Math.cos((-maskedImg.rotation * Math.PI) / 180),
                          },
                        }
                      : item,
                  ),
                },
              }),
            )
            break
          case 'scaling':
            dispatch(
              setAttribute({
                id: interactedImage.id,
                data: {
                  inside: maskedImg.inside.map((item, i) =>
                    i === interactedImage.innerImageIndex && item
                      ? {
                          ...item,
                          scale: clamp(
                            (item.scale as number) -
                              e.nativeEvent.movementY / 100,
                            Config.minScale,
                            Config.maxScale,
                          ),
                        }
                      : item,
                  ),
                },
              }),
            )
            break
          default:
            break
        }
      } else {
        //Main image
        switch (interactedImage.interaction) {
          case 'dragging':
            setMousePreviousPosition({x: e.pageX, y: e.pageY})

            dispatch(
              setAttribute({
                id: image.id,
                data: {
                  ...image,
                  position: {
                    x:
                      image.position.x +
                      movementX / window.devicePixelRatio / zoom.level,
                    y:
                      image.position.y +
                      movementY / window.devicePixelRatio / zoom.level,
                  },
                },
              }),
            )

            break
          case 'scaling':
            let divider = interactedImage.type === 'caption' ? 10 : 80
            let maxScale =
              interactedImage.type === 'caption'
                ? Config.maxTextScale
                : Config.maxScale
            dispatch(
              setScale({
                id: interactedImage.id,
                scale: clamp(
                  (image?.scale as number) - e.nativeEvent.movementY / divider,
                  Config.minScale,
                  maxScale,
                ),
                caption: interactedImage.type === 'caption',
              }),
            )
            break
          case 'rotating':
            dispatch(
              setRotation({
                id: interactedImage.id,
                rotation: (image?.rotation as number) - e.nativeEvent.movementY,
              }),
            )
            break
          default:
            break
        }
      }
    }
  }
  const onMouseLeave = () => {
    addImageHistoryItemByInteraction()
    dispatch(setInteractedImage(null))
    dispatch(setInert({inert: false}))
  }
  const onMouseUp = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    addImageHistoryItemByInteraction()
    dispatch(setInteractedImage(null))
    dispatch(setInert({inert: false}))
  }
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setInitialTouchPosition({x: e.touches[0].pageX, y: e.touches[0].pageY})
      if (interactedImage) {
        let image = images.find(i => i.id === interactedImage.id)
        if (image) {
          setInitialTouchImage({...image})
          const maskedImg = image as ImgMasked
          if (
            interactedImage.innerImageIndex !== undefined &&
            maskedImg.inside !== undefined
          ) {
            const insideImage = maskedImg.inside.find(
              (item, i) => i === interactedImage.innerImageIndex && item,
            )
            if (insideImage) {
              setInitialTouchInsideImage({...insideImage})
            }
          }
        }
      }
    } else if (e.touches.length === 2) {
      setInitialZoomLevel(zoom.level)
      setInitialTouchDistance(
        euclideanDistance(
          e.touches[0].pageX,
          e.touches[0].pageY,
          e.touches[1].pageX,
          e.touches[1].pageY,
        ),
      )
    }
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length === 0) {
      setInitialTouchPosition(null)
      addImageHistoryItemByInteraction()
      dispatch(setInteractedImage(null))
      dispatch(setInert({inert: false}))
      setInitialTouchImage(null)
      setInitialTouchInsideImage(null)
    } else if (e.touches.length === 1) {
      setInitialTouchPosition({x: e.touches[0].pageX, y: e.touches[0].pageY})
      if (interactedImage) {
        let image = images.find(i => i.id === interactedImage.id)
        if (image) {
          setInitialTouchImage({...image})
          const maskedImg = image as ImgMasked
          if (
            interactedImage.innerImageIndex !== undefined &&
            maskedImg.inside !== undefined
          ) {
            const insideImage = maskedImg.inside.find(
              (item, i) => i === interactedImage.innerImageIndex && item,
            )
            if (insideImage) {
              setInitialTouchInsideImage({...insideImage})
            }
          }
        }
      }
    } else if (e.touches.length === 2) {
      setInitialZoomLevel(zoom.level)
      setInitialTouchDistance(
        euclideanDistance(
          e.touches[0].pageX,
          e.touches[0].pageY,
          e.touches[1].pageX,
          e.touches[1].pageY,
        ),
      )
    }
  }
  const onTouchMove = useCallback(
    function (this: HTMLDivElement, e: TouchEvent) {
      if (!initialTouchPosition) {
        return
      }
      if (e.targetTouches.length === 1) {
        // Single touch
        e.preventDefault()
        const deltaX = e.touches[0].pageX - initialTouchPosition.x
        const deltaY = e.touches[0].pageY - initialTouchPosition.y
        if (interactedImage) {
          let image = images.find(i => i.id === interactedImage.id)
          if (!image) {
            return
          }
          let maskedImg = image as ImgMasked
          if (
            interactedImage.innerImageIndex !== undefined &&
            maskedImg.inside !== undefined
          ) {
            //Inside image
            if (initialTouchInsideImage) {
              switch (interactedImage.interaction) {
                case 'dragging':
                  const insideScaleCorrection =
                    (1 / zoom.level / maskedImg.scale) * maskedImg.captionsScale
                  const dx = deltaX * insideScaleCorrection
                  const dy = deltaY * insideScaleCorrection
                  dispatch(
                    setAttribute({
                      id: interactedImage.id,
                      data: {
                        inside: maskedImg.inside.map((item, i) =>
                          i === interactedImage.innerImageIndex && item
                            ? {
                                ...item,
                                position: {
                                  x:
                                    initialTouchInsideImage.position.x +
                                    dx *
                                      Math.cos(
                                        (-maskedImg.rotation * Math.PI) / 180,
                                      ) -
                                    dy *
                                      Math.sin(
                                        (-maskedImg.rotation * Math.PI) / 180,
                                      ),
                                  y:
                                    initialTouchInsideImage.position.y +
                                    dx *
                                      Math.sin(
                                        (-maskedImg.rotation * Math.PI) / 180,
                                      ) +
                                    dy *
                                      Math.cos(
                                        (-maskedImg.rotation * Math.PI) / 180,
                                      ),
                                },
                              }
                            : item,
                        ),
                      },
                    }),
                  )
                  break
                case 'scaling':
                  dispatch(
                    setAttribute({
                      id: interactedImage.id,
                      data: {
                        inside: maskedImg.inside.map((item, i) =>
                          i === interactedImage.innerImageIndex && item
                            ? {
                                ...item,
                                scale: clamp(
                                  initialTouchInsideImage.scale - deltaY / 100,
                                  Config.minScale,
                                  Config.maxScale,
                                ),
                              }
                            : item,
                        ),
                      },
                    }),
                  )
                  break
                default:
                  break
              }
            }
          } else {
            //Main image
            if (initialTouchImage) {
              switch (interactedImage.interaction) {
                case 'dragging':
                  dispatch(
                    setAttribute({
                      id: image.id,
                      data: {
                        ...image,
                        position: {
                          x: initialTouchImage.position.x + deltaX / zoom.level,
                          y: initialTouchImage.position.y + deltaY / zoom.level,
                        },
                      },
                    }),
                  )
                  break
                case 'scaling':
                  dispatch(
                    setScale({
                      id: interactedImage.id,
                      scale: clamp(
                        initialTouchImage.scale - deltaY / 80,
                        Config.minScale,
                        Config.maxScale,
                      ),
                    }),
                  )
                  break
                case 'rotating':
                  dispatch(
                    setRotation({
                      id: interactedImage.id,
                      rotation: initialTouchImage.scale - deltaY,
                    }),
                  )
                  break
                default:
                  break
              }
            }
          }
        }
      } else if (e.targetTouches.length === 2) {
        // Multi-touch
        if (initialTouchDistance && initialZoomLevel) {
          e.preventDefault()
          const touchDistance = euclideanDistance(
            e.touches[0].pageX,
            e.touches[0].pageY,
            e.touches[1].pageX,
            e.touches[1].pageY,
          )
          dispatch(
            setZoom({
              translate: origo,
              level: (initialZoomLevel * touchDistance) / initialTouchDistance,
            }),
          )
        }
      }
    },
    [
      dispatch,
      images,
      interactedImage,
      initialTouchPosition,
      initialTouchImage,
      initialTouchInsideImage,
      zoom.level,
      initialTouchDistance,
      initialZoomLevel,
    ],
  )

  useEffect(() => {
    if (editorRef.current) {
      const editorRefCurrent = editorRef.current

      editorRefCurrent.addEventListener('touchmove', onTouchMove, {
        passive: false,
      })

      return () => {
        editorRefCurrent.removeEventListener('touchmove', onTouchMove)
      }
    }
  }, [onTouchMove])

  const {editor} = useAppSelector(store => store)

  const allImages = pages.reduce(
    (acc, page, index) =>
      acc +
      (index === selectedPageIndex
        ? images.length - 1
        : page.images.length - 1),
    0,
  )
  const printablesPrice =
    ((selectedPageIndex !== undefined &&
    pages[selectedPageIndex].printAreaNativeWidth &&
    pages[selectedPageIndex].printAreaNativeHeight &&
    productPricing
      ? calculatePrice(
          getPrintAreaClientRect(printArea),
          pages[selectedPageIndex].printAreaNativeWidth as number,
          pages[selectedPageIndex].printAreaNativeHeight as number,
          getPrintAreaItemsClientRects(images),
          productPricing,
          selectedPageIndex,
          calendar,
          selectedPageIndex === 1,
          pages.length > 1 && pages[0]?.images?.length > 1,
        )
      : 0) ?? 0) +
    pages.reduce(
      (acc, page, index) =>
        acc + (index === selectedPageIndex ? 0 : page.price),
      0,
    )
  const price =
    !productPricing || printablesPrice === 0
      ? 0
      : productPricing.basePrice + printablesPrice

  const addToCart = async () => {
    setLoading(true)
    try {
      if (selectedColor) {
        const tempPages: AddToCartPage[] = pages.map((page, index) =>
          index === selectedPageIndex
            ? {
                images: images.slice(1),
                printArea: getPrintAreaClientRect(
                  printArea,
                ) as ElementPrintAreaRect,
                printImages: getPrintAreaItemsClientRects(images),
              }
            : {
                images: page.images.slice(1),
                printArea: page.printAreaClientRect as ElementPrintAreaRect,
                printImages: page.imageClientRects,
              },
        )
        const data: AddToCartRequest = {
          productId: productId,
          twoSided: isTwoSided,
          calendar: calendar,
          pages: tempPages,
          editor: editor,
          selectedColor: selectedColor,
          selectedColorUrl: selectedColor.pages[0].imageUrl,
          selectedSize,
        }
        document.cookie = window.parent.document.cookie
        const response = await services.addToCart(data)
        window.parent.location.href = response.data.cartUrl
      }
    } catch (e) {
      setLoading(false)
      console.log('Nem sikerült kosárba rakni')
    }
  }

  const selectedImage = images.find(i => i.id === selectedImageId)
  const imgToolsPosition = (alignRight: boolean = false): StyleSheet => {
    if (selectedImage && editorRef.current) {
      const el = document.getElementById(selectedImage.id)
      if (el) {
        const bcr = el.getBoundingClientRect()
        const bcre = editorRef.current.getBoundingClientRect()
        const left = !alignRight
          ? clamp(bcr.left - bcre.left, 70, bcre.width - 70)
          : clamp(bcr.left - bcre.left + bcr.width, 70, bcre.width - 70)
        const top = clamp(
          bcr.top - bcre.top - 40,
          20,
          bcre.height - 30 - 20 - 74,
        )

        return {
          left: `${left}px`,
          top: `${top}px`,
        } as StyleSheet
      }
    }
    return {}
  }

  return !printArea ? (
    <Lottie
      options={{
        loop: true,
        autoplay: true,
        animationData: LoaderAnimation,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid slice',
        },
      }}
      width={'50%'}
    />
  ) : (
    <>
      <div className={css.editor}>
        {doPrintAreaContentOverflow(images, printArea) ? (
          <p
            style={{
              color: 'red',
              fontWeight: 'bold',
              position: 'absolute',
              right: 48,
              top: 100,
            }}>
            {t('A szerkesztőterületről kilógó grafikák le lesznek vágva!')}
          </p>
        ) : null}
        <ProductPropertyPicker
          colors={colors}
          colorHandler={[selectedColor, setSelectedColor]}
          pages={pages}
          selectedPageIndex={selectedPageIndex}
          setSelectedPageIndex={setSelectedPageIndex}
          sizes={productSizes}
          selectedSize={selectedSize}
          setSelectedSize={setSelectedSize}
        />
        <div
          className={css.editorColumn}
          ref={editorRef}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          onMouseUp={onMouseUp}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}>
          <div className={css.editArea}>
            <div style={{height: '100%', position: 'relative'}} ref={zoomRef}>
              {images.map(image => (
                <RenderImg img={image} key={`editorImageList_${image.id}`} />
              ))}
              {maskImage && (
                <RenderImg
                  className={selectedImageId ? 'maskOpaque' : undefined}
                  img={maskImage}
                  key={`editorMask_${maskImage.id}`}
                  enableMaskFrame
                />
              )}
              {printArea && (
                <RenderImg
                  img={printArea}
                  key={`editorPrintArea_${printArea.id}`}
                  className="print-hidden"
                />
              )}
            </div>
          </div>
          {selectedImage && (
            <ImgTools position={imgToolsPosition()} img={selectedImage} />
          )}
          {selectedImage &&
            selectedImage.type === 'masked' &&
            selectedImage.inside.length > 0 &&
            selectedImage.inside[0].url && (
              <InsideImgTools
                position={imgToolsPosition()}
                img={selectedImage}
                innerImageIndex={0}
              />
            )}
          {selectedImage &&
            selectedImage.type === 'masked' &&
            selectedImage.inside.length > 1 &&
            selectedImage.inside[1].url && (
              <InsideImgTools
                position={imgToolsPosition(true)}
                img={selectedImage}
                innerImageIndex={1}
              />
            )}
          <div className={css.greenPanel}>
            <HistoryButtons />

            <span
              style={{
                color: 'var(--e-global-color-ec5822d)',
                fontWeight: 'bold',
              }}>
              {price
                ? t('{{price}} Ft', {
                    price: price.toLocaleString(i18n.language),
                  })
                : null}
            </span>

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}>
              {doPrintAreaContentOverflow(images, printArea) ? (
                <FontAwesomeIcon
                  icon={faExclamationCircle}
                  title={t(
                    'A szerkesztőterületről kilógó grafikák le lesznek vágva!',
                  )}
                  className={css.overflowAlert}
                />
              ) : null}
              <button
                disabled={allImages === 0 || !price || loading}
                className="editorButton addToCartButton"
                style={{marginLeft: '1rem'}}
                onClick={addToCart}
                onMouseEnter={() => {
                  // This is a dirty fix that forces display price recalculation before adding to cart
                  dispatch(setInert({inert: false}))
                }}>
                {loading ? (
                  <FontAwesomeIcon icon={faSpinner} className={css.spinner} />
                ) : (
                  t('Kosárba')
                )}
              </button>
              <ShareButton
                productId={productId}
                slug={productSlug}
                pages={pages}
                selectedPageIndex={selectedPageIndex}
                productPricing={productPricing}
                calendar={calendar}
                selectedColor={selectedColor}
                selectedSize={selectedSize}
              />
            </div>
          </div>
        </div>
        <div className={css.toolboxColumn}>
          <Toolbox
            color={selectedColor?.colorGraphicsBackground}
            textOnly={textOnly}
          />
        </div>
      </div>
    </>
  )
}
